import type { MenuItem, MenuLink, MenuLinkKind } from '../dataProvider';

/**
 * Admin-side helpers for the navigation menu (D-cms-menu). The menu is an ADJACENCY-LIST
 * tree (parentId + order) CAPPED at 2 LEVELS, grouped by a free `location` key. This module
 * holds the link-kind vocabulary, a link describer and the small tree-shaping helpers the
 * manager and form share.
 */

/** The link kinds (the { kind, ref } shape reused conceptually, NOT imported from blocks.ts). */
export const MENU_LINK_KINDS: readonly MenuLinkKind[] = [
  'page',
  'category',
  'store',
  'external',
];

export const LINK_KIND_LABEL: Record<MenuLinkKind, string> = {
  page: 'Page',
  category: 'Category',
  store: 'Store',
  external: 'External URL',
};

/** A human description of an item's link, or the heading marker when there is none. */
export const describeLink = (link: MenuLink | undefined): string => {
  if (!link) return 'Heading (no link)';
  return `${LINK_KIND_LABEL[link.kind]}: ${link.ref}`;
};

/** The top-level items of a location, order-sorted (parentId null). */
export const topLevel = (items: MenuItem[], location: string): MenuItem[] =>
  items
    .filter((i) => i.location === location && i.parentId === null)
    .sort((a, b) => a.order - b.order);

/** The children of a parent item, order-sorted. */
export const childrenOf = (items: MenuItem[], parentId: number): MenuItem[] =>
  items
    .filter((i) => i.parentId === parentId)
    .sort((a, b) => a.order - b.order);

/** Does this item have children (so the 2-level cap forbids making it a child)? */
export const hasChildren = (items: MenuItem[], id: number): boolean =>
  items.some((i) => i.parentId === id);

/** The distinct locations present, sorted (header before footer by name). */
export const locationsOf = (items: MenuItem[]): string[] =>
  Array.from(new Set(items.map((i) => i.location))).sort();
